import { ConnectionPositionPair, FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, Directive, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, Self } from '@angular/core';
import { NgControl } from '@angular/forms';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { SimpleAutocompleteOverlay } from '../components/simple-autocomplete-overlay/simple-autocomplete-overlay.component';

export interface SimpleAutocompleteSelection<T = any> {
  option: SimpleAutocompleteOption<T>;
  tab: SimpleAutocompleteTab<T>;
}

export class SimpleAutocompleteOption<T = any> {
  private static nextId: number = 1;

  readonly id: number;
  value: T;
  displayValue: string;
  secondaryDisplayValue: string;
  group: string;

  constructor(value: T, displayValue: string, secondaryDisplayValue?: string, group?: string) {
    this.id = SimpleAutocompleteOption.nextId++;
    this.value = value;
    this.displayValue = displayValue;
    this.secondaryDisplayValue = secondaryDisplayValue;
    this.group = group || '';
  }
}

export interface SimpleAutocompleteGroupData<T = any> {
  name: string;
  expanded: boolean;
  options: SimpleAutocompleteOption<T>[];
}

export class SimpleAutocompleteTab<T = any> {
  private static nextId: number = 1;

  private optionsSubject$: BehaviorSubject<SimpleAutocompleteOption<T>[]>;
  readonly options$: Observable<SimpleAutocompleteOption<T>[]>;
  private selectedOption: SimpleAutocompleteOption<T>;
  private selectedGroupHeaderName: string;
  private groupNames: string[];
  private groupMap: Map<string, SimpleAutocompleteGroupData>;
  private collapsedMap: Map<string, void>;
  readonly id: number;
  name: string;

  constructor(name: string, options?: SimpleAutocompleteOption<T>[]) {
    this.id = SimpleAutocompleteTab.nextId++;
    this.name = name;
    this.groupNames = [];
    this.collapsedMap = new Map<string, void>();
    this.groupMap = new Map<string, SimpleAutocompleteGroupData>();
    this.optionsSubject$ = new BehaviorSubject<SimpleAutocompleteOption<T>[]>([]);
    this.options$ = this.optionsSubject$.asObservable();
    this.setOptions(options);
  }

  reset(): void {
    this.selectOption(null);
  }

  getOptions(): SimpleAutocompleteOption<T>[] {
    return this.optionsSubject$.getValue();
  }

  setOptions(options?: SimpleAutocompleteOption<T>[]): void {
    options = options || [];
    let groupNames: string[] = [];
    let groupMap: Map<string, SimpleAutocompleteGroupData> = new Map<string, SimpleAutocompleteGroupData>();
    options.forEach((option: SimpleAutocompleteOption<T>) => {
      const groupName: string = option.group;
      let groupData: SimpleAutocompleteGroupData<T> = groupMap.get(groupName);
      if(!groupData) {
        groupData = this.groupMap.get(groupName);
        if(!groupData) {
          groupData = {
            name: groupName,
            expanded: !this.collapsedMap.has(groupName),
            options: []
          }
        } else {
          groupData = {
            ...groupData,
            options: []
          };
        }
        groupMap.set(groupName, groupData);
        groupNames.push(groupName);
      }
      groupData.options.push(option);
    });
    groupNames.sort((a: string, b: string): number => (b == '' ? 1 : 0) - (a == '' ? 1 : 0));
    this.optionsSubject$.next(options);
    this.groupMap = groupMap;
    this.groupNames = groupNames;
    this.selectOption(null);
  }

  getGroupNames(): string[] {
    return this.groupNames;
  }

  getGroupData(groupName: string): SimpleAutocompleteGroupData {
    return this.groupMap.get(groupName);
  }

  toggleExpanded(groupName: string): void {
    const groupData: SimpleAutocompleteGroupData = this.getGroupData(groupName);
    if(groupData) {
      groupData.expanded = !groupData.expanded;
      if(groupData.expanded)
        this.collapsedMap.delete(groupData.name);
      else {
        this.collapsedMap.set(groupData.name);
        if(this.selectedOption && this.selectedOption.group == groupData.name)
          this.selectOption(null);
      }
    }
  }

  isHidden(): boolean {
    return this.getOptions().length == 0;
  }

  selectOption(option: SimpleAutocompleteOption<T>): void {
    this.selectedGroupHeaderName = null;
    this.selectedOption = option;
  }

  optionIsSelected(option: SimpleAutocompleteOption<T>): boolean {
    return option == null ? this.selectedOption == null : this.selectedOption?.id == option.id;
  }

  selectGroup(groupName: string): void {
    this.selectedOption = null;
    this.selectedGroupHeaderName = groupName;
  }

  groupIsSelected(groupName: string): boolean {
    return this.selectedGroupHeaderName == groupName;
  }

  getSelectedOption(): SimpleAutocompleteOption<T> {
    return this.selectedOption;
  }

  getSelectedGroupHeaderName(): string {
    return this.selectedGroupHeaderName;
  }
}

@Directive({
  selector: '[SimpleAutoComplete]'
})
export class SimpleAutocomplete<T = any> implements OnDestroy {
  private tabs: SimpleAutocompleteTab<T>[];
  private selectedTab: SimpleAutocompleteTab<T>;
  private preferredTab: SimpleAutocompleteTab<T>;
  private tabOptionsSubscriptions: Subscription[];

  constructor(@Inject('tabs')...tabs: (string | SimpleAutocompleteTab<T>)[]) {
    this.setTabs(tabs);
  }

  ngOnDestroy(): void {
    this.tabOptionsSubscriptions?.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  reset(): void {
    this.selectedTab = null;
    this.preferredTab = null;
    this.tabs.forEach((tab: SimpleAutocompleteTab<T>) => tab.reset());
    this.autoSelectTab();
  }

  getTabs(): SimpleAutocompleteTab<T>[] {
    return this.tabs;
  }

  autoSelectTab(): void {
    this.selectTab(this.tabs.find((tab: SimpleAutocompleteTab<T>): boolean => !tab.isHidden()), false);
  }

  setTabs(tabs?: (string | SimpleAutocompleteTab<T>)[]): void {
    this.tabs = this.parseMixedTabs(tabs);
    this.tabOptionsSubscriptions?.forEach((subscription: Subscription) => subscription.unsubscribe());
    this.tabOptionsSubscriptions = this.tabs.map((tab: SimpleAutocompleteTab): Subscription => tab.options$.subscribe(() => this.tabOptionsChanged(tab)));
    if(!this.selectedTab || this.tabs.findIndex((tab: SimpleAutocompleteTab<T>): boolean => this.tabIsSelected(tab)) < 0)
      this.autoSelectTab();
    if(this.preferredTab && this.tabs.findIndex((tab: SimpleAutocompleteTab<T>): boolean => tab.id == this.preferredTab.id) < 0)
      this.preferredTab = null;
  }

  tabOptionsChanged(tab: SimpleAutocompleteTab<T>): void {
    const options: SimpleAutocompleteOption<T>[] = tab.getOptions();
    if(options.length > 0) {
      if(this.preferredTab && tab.id == this.preferredTab.id)
        this.selectTab(tab);
      else if(!this.selectedTab)
        this.autoSelectTab();
    } else {
      if(this.tabIsSelected(tab))
        this.autoSelectTab();
    }
  }

  selectTab(tab: SimpleAutocompleteTab<T>, preferred: boolean = true): void {
    if(preferred)
      this.preferredTab = null;
    else if(!this.preferredTab)
      this.preferredTab = this.selectedTab;
    if(!this.tabIsSelected(tab))
      this.selectedTab = tab;
    if(this.tabIsSelected(this.preferredTab))
      this.preferredTab = null;
  }

  tabIsSelected(tab: SimpleAutocompleteTab<T>): boolean {
    return tab == null ? this.selectedTab == null : this.selectedTab?.id == tab.id;
  }

  getSelectedTab(): SimpleAutocompleteTab<T> {
    return this.selectedTab;
  }

  private parseMixedTabs(tabs?: (string | SimpleAutocompleteTab<T>)[]): SimpleAutocompleteTab<T>[] {
    if(!(tabs?.length > 0))
      tabs = [''];
    return tabs.map((tab: string | SimpleAutocompleteTab<T>): SimpleAutocompleteTab<T> => (tab != null && tab instanceof SimpleAutocompleteTab) ? tab : new SimpleAutocompleteTab<T>((tab || '') as string));
  }
}

@Directive({
  selector: '[simpleAutocomplete]'
})
export class SimpleAutocompleteDirective implements OnInit, OnDestroy {
  @Output() optionSelected: EventEmitter<SimpleAutocompleteSelection>;
  @Output() opened: EventEmitter<void>;
  @Output() closed: EventEmitter<void>;
  @Input() set simpleAutocomplete(autocomplete: SimpleAutocomplete) {
    this.autocomplete = autocomplete;
    this.componentRef?.instance.setAutocomplete(autocomplete);
  }

  get simpleAutocomplete(): SimpleAutocomplete {
    return this.autocomplete;
  }

  private overlayRef: OverlayRef;
  private componentRef: ComponentRef<SimpleAutocompleteOverlay>;
  private selectionSubscription: Subscription;
  private valueSubscription: Subscription;
  private autocomplete: SimpleAutocomplete;
  private isFocused: boolean;
  private settingValue: boolean;
  isOpen: boolean;

  constructor(@Self() private ngControl: NgControl, private element: ElementRef, private overlay: Overlay) {
    this.isFocused = false;
    this.isOpen = false;
    this.settingValue = false;
    this.optionSelected = new EventEmitter<SimpleAutocompleteSelection>();
    this.opened = new EventEmitter<void>();
    this.closed = new EventEmitter<void>();
  }

  ngOnInit(): void {
    this.element.nativeElement.addEventListener('focus', this.focus);
    this.element.nativeElement.addEventListener('blur', this.unfocus);
    this.element.nativeElement.addEventListener('keydown', this.handleKeydown);
    this.valueSubscription = this.ngControl.valueChanges.subscribe(() => {
      if(this.isFocused && !this.settingValue)
        this.open();
    });
  }

  ngOnDestroy(): void {
    this.element.nativeElement.removeEventListener('focus', this.focus);
    this.element.nativeElement.removeEventListener('blur', this.unfocus);
    this.element.nativeElement.removeEventListener('keydown', this.handleKeydown);
    this.valueSubscription?.unsubscribe();
    if(this.isOpen)
      this.close();
  }

  private focus: () => void = (): void => {
    this.isFocused = true;
    this.open();
  }

  private unfocus: () => void = (): void => {
    this.isFocused = false;
    this.close();
  }

  private handleKeydown: (event: KeyboardEvent) => void = (event: any): void => {
    if(!this.isOpen)
      return;
    switch(event.key) {
      case 'ArrowUp':
        event.preventDefault();
        this.componentRef?.instance.up();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.componentRef?.instance.down();
        break;
      case 'Enter':
        event.preventDefault();
        this.componentRef?.instance.submit();
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      default:
    }
  } 

  private getOverlayPosition(): FlexibleConnectedPositionStrategy {
    const positions = [
      new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'start', overlayY: 'top' }),
      new ConnectionPositionPair({ originX: 'start', originY: 'top' }, { overlayX: 'start', overlayY: 'bottom' })
    ];
    return this.overlay
      .position()
      .flexibleConnectedTo(this.element.nativeElement)
      .withPositions(positions)
      .withFlexibleDimensions(false)
      .withPush(false);
  }

  open(): void {
    if(this.isOpen)
      return;
    this.isOpen = true;
    this.autocomplete?.reset();
    this.overlayRef = this.overlay.create({
      width: this.element.nativeElement.offsetWidth,
      panelClass: 'simple-autocomplete-overlay-panel',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      positionStrategy: this.getOverlayPosition()
    });
    this.componentRef = this.overlayRef.attach(new ComponentPortal(SimpleAutocompleteOverlay));
    this.componentRef.instance.setAutocomplete(this.autocomplete);
    this.selectionSubscription = this.componentRef.instance.selections$.subscribe((selection: SimpleAutocompleteSelection) => {
      this.close();
      this.settingValue = true;
      this.ngControl.control.setValue(selection.option.displayValue);
      this.settingValue = false;
      this.optionSelected.emit(selection);
    });
    this.opened.emit();
  }

  close(): void {
    if(!this.isOpen)
      return;
    this.isOpen = false;
    this.selectionSubscription?.unsubscribe();
    this.overlayRef?.detach();
    this.overlayRef = null;
    this.autocomplete?.reset();
    this.closed.emit();
  }
}